import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { entraApiScope, msalInstance } from './msal.ts';
import { getStoredCreatorSessionToken } from './sessionToken.ts';

export class MsalNotInitializedError extends Error {
  constructor(message = 'MSAL is not initialized') {
    super(message);
    this.name = 'MsalNotInitializedError';
  }
}

function isValidTokenShape(token: unknown): token is string {
  if (typeof token !== 'string') return false;
  const dotCount = (token.match(/\./g) || []).length;
  if (dotCount !== 2) return false;
  if (token.startsWith('{') || token.startsWith('[')) return false;
  if (token.includes('Bearer ')) return false;
  return true;
}

function logInvalidTokenShape(token: unknown): void {
  const raw = typeof token === 'string' ? token : String(token);
  const dotCount = typeof token === 'string' ? (token.match(/\./g) || []).length : -1;
  // Never log token contents; only safe diagnostics.
  // eslint-disable-next-line no-console
  console.warn('[auth] Invalid token shape', {
    tokenType: typeof token,
    tokenLength: raw.length,
    dotCount,
    startsWithBrace: raw.startsWith('{') || raw.startsWith('['),
    containsBearerPrefix: raw.includes('Bearer ')
  });
}

export async function getApiAccessToken(): Promise<string> {
  const storedSessionToken = getStoredCreatorSessionToken();
  if (storedSessionToken) return storedSessionToken;

  if (!msalInstance) throw new MsalNotInitializedError();

  const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  if (!account) throw new Error('No active account');

  try {
    const result = await msalInstance.acquireTokenSilent({
      account,
      scopes: [entraApiScope]
    });
    const token = result?.accessToken;
    if (!isValidTokenShape(token)) {
      logInvalidTokenShape(token);
      throw new Error('Invalid access token shape from acquireTokenSilent');
    }
    return token;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      throw e;
    }
    throw e;
  }
}

